import { useMemo } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import {
  Button,
  Label,
  Input,
  Checkbox,
  Alert,
  AlertDescription,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from '@predoc/cow';
import { useForm, ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { DataDeletionRequestLevel, DataDeletionFormState } from '../types';
import { useRequestDataDeletion } from '../hooks/organizationSettingsApi';
import {
  confirmationStepSchema,
  ConfirmationStepFormType,
} from '../validation';

interface ConfirmationStepProps {
  formState: Omit<DataDeletionFormState, 'deletionDate'> & {
    deletionDate: Date;
  };
  setFormState: React.Dispatch<React.SetStateAction<DataDeletionFormState>>;
  onCancel: () => void;
  onBack: () => void;
  onSuccess: () => void;
  totalPatientCount: number;
}

export const ConfirmationStep = ({
  formState,
  setFormState,
  onCancel,
  onBack,
  onSuccess,
  totalPatientCount,
}: ConfirmationStepProps) => {
  const requestDeletionMutation = useRequestDataDeletion();

  const form = useForm<ConfirmationStepFormType>({
    resolver: zodResolver(confirmationStepSchema),
    defaultValues: {
      confirmText: '',
      isExportData: formState.isExportData,
    },
    mode: 'onChange',
  });

  const watchedConfirmText = form.watch('confirmText');
  const isConfirmValid = watchedConfirmText === 'Delete';
  const confirmTextError = form.formState.errors.confirmText?.message;

  const patientCount = useMemo(() => {
    if (formState.deletionType === DataDeletionRequestLevel.SPECIFIC_PATIENTS) {
      return formState.selectedPatients.length;
    }
    if (formState.deletionType === DataDeletionRequestLevel.SPECIFIC_PROFILES) {
      return formState.selectedProfiles.reduce(
        (sum, profile) => sum + profile.patientCount,
        0,
      );
    }
    return totalPatientCount;
  }, [formState, totalPatientCount]);

  const alertMessage = `Confirm data deletion request for (${patientCount}) patients.`;

  const handleExportDataChange = (checked: boolean) => {
    form.setValue('isExportData', checked);
    setFormState((prev) => ({
      ...prev,
      isExportData: checked,
    }));
  };

  const handleSubmit = form.handleSubmit(async () => {
    try {
      const requestIds =
        formState.deletionType === DataDeletionRequestLevel.SPECIFIC_PATIENTS
          ? formState.selectedPatients.map((p) => p.id)
          : formState.deletionType ===
              DataDeletionRequestLevel.SPECIFIC_PROFILES
            ? formState.selectedProfiles.map((p) => p.id)
            : undefined;

      await requestDeletionMutation.mutateAsync({
        level: formState.deletionType,
        requestIds: requestIds as [string, ...string[]] | undefined,
        needByDate: format(formState.deletionDate, 'yyyy-MM-dd'),
        exportBeforeDeletion: form.getValues('isExportData'),
      });

      toast('Data deletion request successfully submitted.', {
        description: (
          <span>
            A member of our team will contact you to confirm and initiate the
            deletion process. Please reach out to{' '}
            <a href="mailto:support@predoc.ai" className="underline">
              support@predoc.ai
            </a>{' '}
            with any questions.
          </span>
        ),
        duration: 8000,
      });

      onSuccess();
    } catch {
      toast.error('Failed to submit data deletion request.');
    }
  });

  return (
    <>
      <div className="px-6 py-2">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="size-4" />
          <AlertDescription>
            <p className="text-base font-medium">{alertMessage}</p>
            <p>
              We will initiate the deletion for all patient data you selected.{' '}
              <strong>This cannot be undone.</strong>
            </p>
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <div className="mb-6 w-1/2">
            <FormField
              control={form.control}
              name="confirmText"
              render={({
                field,
              }: {
                field: ControllerRenderProps<
                  ConfirmationStepFormType,
                  'confirmText'
                >;
              }) => (
                <FormItem>
                  <Label className={cn(confirmTextError && 'text-destructive')}>
                    Confirm by Typing &quot;Delete&quot;
                  </Label>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        id="confirm-delete"
                        placeholder="Delete"
                        className={cn(
                          'focus-visible:ring-0',
                          confirmTextError
                            ? 'border-destructive focus-visible:border-destructive'
                            : 'focus-visible:border-input',
                        )}
                      />
                    </FormControl>
                    {isConfirmValid ? (
                      <span className="border-success flex h-5 w-5 items-center justify-center rounded-full border-2">
                        <Check
                          className="text-success size-3"
                          strokeWidth={4}
                        />
                      </span>
                    ) : null}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-2">
            <Checkbox
              id="export-data"
              checked={form.watch('isExportData')}
              onCheckedChange={handleExportDataChange}
              className="mt-1"
            />
            <div>
              <Label>Export a copy of the data (Optional)</Label>
              <p className="text-muted-foreground text-sm">
                Predoc will export and share with you a copy of the deleted data
                for future retrieval and compliance. Do not check this box if
                you need complete deletion of selected patient data.
              </p>
            </div>
          </div>
        </Form>
      </div>

      <div className="flex justify-between border-t px-6 py-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isConfirmValid || requestDeletionMutation.isPending}
          >
            {requestDeletionMutation.isPending
              ? 'Submitting...'
              : 'Request Deletion'}
          </Button>
        </div>
      </div>
    </>
  );
};
